import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ModuleOnboarding from '@/components/ModuleOnboarding';
import { projectsOnboardingSteps, projectsOnboardingBenefits } from '@/data/onboardingConfigs';
import { NewProjectDialog } from '@/components/NewProjectDialog';
import {
  Target, HelpCircle, Calendar, Building2, Plus, FlaskConical, Trophy,
  Search, Filter, Users, FileText, Bot, Zap, BarChart3, Code, Sparkles, Loader2, PackageCheck, X
} from 'lucide-react';
import { useProjects, solutionTypeToKey } from '@/hooks/useProjects';
import { useAuth } from '@/contexts/AuthContext';

const SOLUTION_TYPES = {
  content: { id: 'content', label: 'Creación de Contenido con IA', icon: FileText, color: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
  vibecoding: { id: 'vibecoding', label: 'Vibecoding y Creación de Aplicaciones con IA', icon: Code, color: 'bg-purple-500/10 text-purple-600 border-purple-500/20' },
  assistant: { id: 'assistant', label: 'Asistente/ Agente IA', icon: Bot, color: 'bg-green-500/10 text-green-600 border-green-500/20' },
  automation: { id: 'automation', label: 'Automatización IA', icon: Zap, color: 'bg-orange-500/10 text-orange-600 border-orange-500/20' },
  data: { id: 'data', label: 'Análisis de Datos con IA', icon: BarChart3, color: 'bg-cyan-500/10 text-cyan-600 border-cyan-500/20' },
};

const statusConfig: Record<string, { label: string; color: string }> = {
  ideation: { label: 'Ideación', color: 'bg-muted/50 text-muted-foreground border-border' },
  planning: { label: 'Planificación', color: 'bg-amber-500/10 text-amber-600 border-amber-500/20' },
  in_progress: { label: 'En progreso', color: 'bg-primary/10 text-primary border-primary/20' },
  review: { label: 'En revisión', color: 'bg-secondary/10 text-secondary border-secondary/20' },
  completed: { label: 'Completado', color: 'bg-success/10 text-success border-success/20' },
};

const PROJECTS_ONBOARDING_KEY = 'projects_onboarding_completed';

export function ChallengePage() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { data: projects, isLoading } = useProjects();
  const orgName = profile?.company;

  const [searchParams, setSearchParams] = useSearchParams();
  const [showDeliverableHint, setShowDeliverableHint] = useState(false);

  useEffect(() => {
    if (searchParams.get('onboarding') === 'deliverable') {
      setShowDeliverableHint(true);
      const next = new URLSearchParams(searchParams);
      next.delete('onboarding');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const [showOnboarding, setShowOnboarding] = useState(() => !localStorage.getItem(PROJECTS_ONBOARDING_KEY));
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSolutionType, setFilterSolutionType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const handleOnboardingComplete = () => {
    localStorage.setItem(PROJECTS_ONBOARDING_KEY, 'true');
    setShowOnboarding(false);
  };

  const handleShowOnboarding = () => {
    localStorage.removeItem(PROJECTS_ONBOARDING_KEY);
    setShowOnboarding(true);
  };

  const filteredProjects = useMemo(() => {
    if (!projects) return [];
    return projects.filter(project => {
      const sKey = solutionTypeToKey(project.solution_type);
      const matchesSearch = searchQuery === '' ||
        project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (project.problem || '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesSolutionType = filterSolutionType === 'all' || sKey === filterSolutionType;
      const matchesStatus = filterStatus === 'all' || project.status === filterStatus;
      return matchesSearch && matchesSolutionType && matchesStatus;
    });
  }, [projects, searchQuery, filterSolutionType, filterStatus]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (showOnboarding) {
    return (
      <ModuleOnboarding
        steps={projectsOnboardingSteps}
        benefits={projectsOnboardingBenefits}
        onComplete={handleOnboardingComplete}
        ctaText="Explorar Proyectos"
      />
    );
  }

  return (
    <div className="space-y-6">
      {showDeliverableHint && (
        <div className="flex items-start gap-3 rounded-xl border border-primary/30 bg-primary/5 p-4">
          <PackageCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
          <p className="text-sm text-foreground flex-1">
            Ingresa al proyecto que deseas enviar, sube el entregable y responde las preguntas para
            evidenciar que lo has terminado.
          </p>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={() => setShowDeliverableHint(false)}
            aria-label="Cerrar aviso"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-primary flex items-center justify-center">
              <Target className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-foreground">Proyectos</h1>
            <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground" onClick={handleShowOnboarding} title="Ver ayuda">
              <HelpCircle className="w-5 h-5" />
            </Button>
          </div>
          <p className="text-muted-foreground">Define, desarrolla y gestiona tus proyectos de IA</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" className="gap-2" onClick={() => navigate('/challenge/showcase')}>
            <Trophy className="w-4 h-4" /> <span className="hidden sm:inline">Showcase</span>
          </Button>
          <Button variant="outline" className="gap-2" onClick={() => navigate('/challenge-lab')}>
            <FlaskConical className="w-4 h-4" /> <span className="hidden sm:inline">Laboratorio</span>
          </Button>
          <NewProjectDialog />
        </div>
      </div>

      {/* Filters */}
      <Card className="glass-card border-0 shadow-md">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Buscar proyectos..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
            </div>
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-muted-foreground" />
              <Select value={filterSolutionType} onValueChange={setFilterSolutionType}>
                <SelectTrigger className="w-full sm:w-[280px]"><SelectValue placeholder="Tipo de Solución" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los tipos</SelectItem>
                  {Object.entries(SOLUTION_TYPES).map(([key, type]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2"><type.icon className="w-4 h-4" /><span className="truncate">{type.label}</span></div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full sm:w-[180px]"><SelectValue placeholder="Estado" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  {Object.entries(statusConfig).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {isLoading ? 'Cargando...' : `${filteredProjects.length} proyecto${filteredProjects.length !== 1 ? 's' : ''} encontrado${filteredProjects.length !== 1 ? 's' : ''}`}
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* Projects Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project) => {
              const sKey = solutionTypeToKey(project.solution_type);
              const solutionType = SOLUTION_TYPES[sKey as keyof typeof SOLUTION_TYPES];
              const SolutionIcon = solutionType?.icon || Sparkles;
              const status = statusConfig[project.status || 'ideation'] || statusConfig.ideation;

              return (
                <Card
                  key={project.id}
                  className="glass-card border-0 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group"
                  onClick={() => navigate(`/project/${project.id}`)}
                >
                  <CardContent className="p-6">
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${status.color}`}>
                        {status.label}
                      </span>
                      {solutionType && (
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${solutionType.color}`}>
                          <SolutionIcon className="w-3 h-3 flex-shrink-0" />
                          <span>{solutionType.label}</span>
                        </span>
                      )}
                    </div>
                    <h3 className="text-lg font-bold text-foreground mb-2 group-hover:text-primary transition-colors">
                      {project.name}
                    </h3>
                    <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                      {project.problem || 'Sin descripción del problema'}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground pt-4 border-t border-border">
                      <div className="flex items-center gap-1.5">
                        <Building2 className="w-3.5 h-3.5" />
                        <span className="truncate">{orgName || 'Mi empresa'}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5" />
                        <span>{formatDate(project.created_at)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            <NewProjectDialog
              trigger={
                <Card className="glass-card border-2 border-dashed border-muted-foreground/30 hover:border-primary/50 transition-all duration-300 cursor-pointer group">
                  <CardContent className="p-6 flex flex-col items-center justify-center h-full min-h-[280px]">
                    <div className="w-16 h-16 rounded-full bg-muted/50 group-hover:bg-primary/10 flex items-center justify-center mb-4 transition-colors">
                      <Plus className="w-8 h-8 text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                    <h3 className="text-lg font-semibold text-muted-foreground group-hover:text-foreground transition-colors mb-2">Nuevo Proyecto</h3>
                    <p className="text-sm text-muted-foreground text-center">Crea un nuevo proyecto de IA</p>
                  </CardContent>
                </Card>
              }
            />
          </div>

          {filteredProjects.length === 0 && !isLoading && (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">No se encontraron proyectos</h3>
              <p className="text-muted-foreground mb-4">Intenta ajustar los filtros o crear un nuevo proyecto</p>
              <Button className="gradient-primary gap-2" onClick={() => { setSearchQuery(''); setFilterSolutionType('all'); setFilterStatus('all'); }}>
                Limpiar filtros
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
